import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Eye, FileDown } from "lucide-react";
import { downloadFeeReceipt } from "@/lib/feeExport";
import { inr, fdate, REQ_LABEL, REQ_COLOR } from "@/components/FeeLedgerDialog";

export function ProofImage({ requestId, className }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let u = "";
    api.get(`/fee-mgmt/requests/${requestId}/proof`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [requestId]);
  return url ? <img data-testid={`proof-${requestId}`} src={url} alt="Payment screenshot" className={className} /> : <div className={`${className} bg-slate-100 animate-pulse`} />;
}

const NEXT = ["under_review", "approved", "payment_pending", "payment_verified", "rejected"];

function ReviewDialog({ req, onClose, onDone }) {
  const [status, setStatus] = useState("under_review");
  const [note, setNote] = useState("");
  useEffect(() => { if (req) { setStatus(req.status === "submitted" ? "under_review" : "payment_verified"); setNote(""); } }, [req]);
  async function save() {
    try { await api.patch(`/fee-mgmt/requests/${req.id}`, { status, note }); toast.success(`Marked ${REQ_LABEL[status]}`); onDone(); onClose(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <Dialog open={!!req} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-xl">Review payment request</DialogTitle></DialogHeader>
        {req && (
          <div className="grid sm:grid-cols-2 gap-4">
            <ProofImage requestId={req.id} className="w-full max-h-[60vh] object-contain rounded-xl border" />
            <div className="space-y-2 text-sm">
              <div><b>{req.student_name}</b> • {req.class_name} {req.section}</div>
              <div>Installment {req.installment_no} (due {fdate(req.installment_due)})</div>
              <div>Amount <b>{inr(req.amount)}</b></div>
              <div>Transaction ID <span className="font-mono">{req.transaction_id}</span></div>
              <div>Parent {req.parent_name} • {fdate(req.created_at)}</div>
              {req.note && <div className="text-slate-600">"{req.note}"</div>}
              <div className="pt-2 space-y-1">
                {req.history.map((h, i) => <div key={i} className="text-xs text-slate-500">{REQ_LABEL[h.status]} by {h.by} • {fdate(h.at)}{h.note ? ` — ${h.note}` : ""}</div>)}
              </div>
              <div className="pt-3 space-y-2">
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="review-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{NEXT.map((s) => <SelectItem key={s} value={s}>{REQ_LABEL[s]}</SelectItem>)}</SelectContent>
                </Select>
                <Input data-testid="review-note" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
                <p className="text-xs text-slate-500">Only "Payment Verified" updates the student's ledger and creates a receipt.</p>
                <Button data-testid="review-save" onClick={save} className="w-full bg-violet-600 hover:bg-violet-700">Update request</Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function RequestsPanel({ branchId }) {
  const { user } = useAuth();
  const canManage = ["master_admin", "super_admin", "accountant"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("open");
  const [review, setReview] = useState(null);
  const load = () => api.get("/fee-mgmt/requests", { params: { ...(branchId ? { branch_id: branchId } : {}), ...(status !== "open" && status !== "all" ? { status } : {}) } })
    .then((r) => setRows(status === "open" ? r.data.filter((x) => !["payment_verified", "rejected"].includes(x.status)) : r.data)).catch(() => {});
  useEffect(() => { load(); }, [branchId, status]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-3">
      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger data-testid="requests-filter" className="w-56 bg-white"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="open">Open requests</SelectItem><SelectItem value="all">All requests</SelectItem>
          {Object.keys(REQ_LABEL).filter((k) => k !== "verifying").map((k) => <SelectItem key={k} value={k}>{REQ_LABEL[k]}</SelectItem>)}
        </SelectContent>
      </Select>
      {rows.length === 0 ? <p className="text-sm text-slate-500 py-8 text-center bg-white rounded-2xl">No payment requests.</p> : (
        <div className="bg-white rounded-2xl border border-violet-100 divide-y divide-slate-100">
          {rows.map((q) => (
            <div key={q.id} data-testid={`request-${q.id}`} className="p-3 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[200px]">
                <div className="font-semibold text-slate-900">{q.student_name} <span className="text-xs text-slate-500">• {q.class_name} {q.section} • Inst {q.installment_no}</span></div>
                <div className="text-xs text-slate-500">{inr(q.amount)} • Txn {q.transaction_id} • {q.parent_name} • {fdate(q.created_at)}</div>
              </div>
              <Badge className={REQ_COLOR[q.status]}>{REQ_LABEL[q.status]}</Badge>
              {q.payment_id && <Button size="sm" variant="outline" data-testid={`request-receipt-${q.id}`} onClick={() => downloadFeeReceipt(q.payment_id)}><FileDown size={14} /></Button>}
              <Button size="sm" variant="outline" data-testid={`request-review-${q.id}`} onClick={() => setReview(q)} disabled={!canManage && false}><Eye size={14} className="mr-1" /> {canManage && !["payment_verified", "rejected"].includes(q.status) ? "Review" : "View"}</Button>
            </div>
          ))}
        </div>
      )}
      {canManage ? <ReviewDialog req={review} onClose={() => setReview(null)} onDone={load} /> : (
        <Dialog open={!!review} onOpenChange={(v) => { if (!v) setReview(null); }}>
          <DialogContent>{review && <ProofImage requestId={review.id} className="w-full max-h-[70vh] object-contain rounded-xl" />}</DialogContent>
        </Dialog>
      )}
    </div>
  );
}
